import React, { useEffect, useRef } from "react";
import { useEditor, EditorContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import { Button } from "@/components/ui/button";
import { Bold, Italic, List, ListOrdered, AlignLeft, AlignCenter, AlignRight } from "lucide-react";

// Define extensions at module level to ensure they're created once and shared across all instances
// This prevents "Adding different instances of a keyed plugin" errors
const extensions = [
  StarterKit.configure({
    bulletList: {
      HTMLAttributes: {
        class: "list-disc pl-6 my-2",
      },
    },
    orderedList: {
      HTMLAttributes: {
        class: "list-decimal pl-6 my-2",
      },
    },
    listItem: {
      HTMLAttributes: {
        class: "my-1",
      },
    },
  }),
  TextAlign.configure({
    types: ["heading", "paragraph"],
    alignments: ["left", "center", "right"],
    defaultAlignment: "left",
  }),
];

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  className?: string;
  placeholder?: string;
  editorKey?: string; // Add unique key to ensure separate plugin instances
  onAIContentInsert?: (insertContent: (content: string) => void) => void; // Callback to expose AI content insertion method
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  className = "",
  placeholder = "Start typing...",
  editorKey: propEditorKey,
  onAIContentInsert,
}) => {
  const isInitialMount = useRef(true);
  const isUpdatingContent = useRef(false);
  const editorKey = useRef(propEditorKey || Math.random().toString(36).substr(2, 9));

  const editor = useEditor(
    {
      extensions,
      content,
      onUpdate: ({ editor }) => {
        // Prevent onChange from being called during initial mount or programmatic content updates
        if (!isInitialMount.current && !isUpdatingContent.current) {
          onChange(editor.getHTML());
        }
      },
      editorProps: {
        attributes: {
          class: "min-h-[150px] p-3 focus:outline-none",
          placeholder,
        },
      },
      onCreate: ({ editor }) => {
        console.log(`🚀 TipTap editor created for key: ${editorKey.current}`);
      },
      onDestroy: () => {
        console.log(`🚀 TipTap editor destroyed for key: ${editorKey.current}`);
      },
    },
    []
  );

  // Mark that initial mount is complete and cleanup on unmount
  useEffect(() => {
    if (editor) {
      isInitialMount.current = false;
    }

    return () => {
      if (editor) {
        editor.destroy();
      }
    };
  }, [editor]);

  // Expose AI content insertion method to parent component
  useEffect(() => {
    if (editor && onAIContentInsert) {
      const insertAIContent = (content: string) => {
        if (editor && !editor.isDestroyed) {
          // Use insertContent instead of setContent for AI-generated content
          // This approach is optimized for AI content and should avoid plugin conflicts
          isUpdatingContent.current = true;
          editor.commands.insertContent(content);
          // Reset the flag after a brief delay to allow the update to complete
          setTimeout(() => {
            isUpdatingContent.current = false;
          }, 0);
        }
      };
      onAIContentInsert(insertAIContent);
    }
  }, [editor, onAIContentInsert]);

  // Update editor content when the content prop changes
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      isUpdatingContent.current = true;
      editor.commands.setContent(content);
      // Reset the flag after a brief delay to allow the update to complete
      setTimeout(() => {
        isUpdatingContent.current = false;
      }, 0);
    }
  }, [content, editor]);

  if (!editor) {
    return <div className="rounded-md border p-3">Loading editor...</div>;
  }

  const toggleBold = () => {
    editor.chain().focus().toggleBold().run();
  };

  const toggleItalic = () => {
    editor.chain().focus().toggleItalic().run();
  };

  const toggleBulletList = () => {
    editor.chain().focus().toggleBulletList().run();
  };

  const toggleOrderedList = () => {
    editor.chain().focus().toggleOrderedList().run();
  };

  const setTextAlign = (align: "left" | "center" | "right") => {
    editor.chain().focus().setTextAlign(align).run();
  };

  return (
    <div className={`rounded-md border ${className}`}>
      <div className="flex flex-wrap gap-1 border-b bg-gray-50 p-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={toggleBold}
          className={`h-8 w-8 p-1 ${editor.isActive("bold") ? "bg-gray-200" : ""}`}
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={toggleItalic}
          className={`h-8 w-8 p-1 ${editor.isActive("italic") ? "bg-gray-200" : ""}`}
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={toggleBulletList}
          className={`h-8 w-8 p-1 ${editor.isActive("bulletList") ? "bg-gray-200" : ""}`}
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={toggleOrderedList}
          className={`h-8 w-8 p-1 ${editor.isActive("orderedList") ? "bg-gray-200" : ""}`}
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
        <div className="mx-1 h-8 border-l"></div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setTextAlign("left")}
          className={`h-8 w-8 p-1 ${editor.isActive({ textAlign: "left" }) ? "bg-gray-200" : ""}`}
        >
          <AlignLeft className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setTextAlign("center")}
          className={`h-8 w-8 p-1 ${editor.isActive({ textAlign: "center" }) ? "bg-gray-200" : ""}`}
        >
          <AlignCenter className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setTextAlign("right")}
          className={`h-8 w-8 p-1 ${editor.isActive({ textAlign: "right" }) ? "bg-gray-200" : ""}`}
        >
          <AlignRight className="h-4 w-4" />
        </Button>
      </div>
      <EditorContent editor={editor} className="prose prose-sm max-w-none p-3" />
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .ProseMirror {
            min-height: 150px;
            outline: none;
          }
          .ProseMirror ul {
            list-style-type: disc;
            padding-left: 1.5rem;
            margin: 0.5rem 0;
          }
          .ProseMirror ol {
            list-style-type: decimal;
            padding-left: 1.5rem;
            margin: 0.5rem 0;
          }
          .ProseMirror li {
            margin: 0.2rem 0;
          }
          .ProseMirror p {
            margin: 0.5rem 0;
          }
          .ProseMirror [style*="text-align:center"] {
            text-align: center;
          }
          .ProseMirror [style*="text-align:right"] {
            text-align: right;
          }
          .ProseMirror [style*="text-align:left"] {
            text-align: left;
          }
        `,
        }}
      />
    </div>
  );
};

export default RichTextEditor;
